import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, ReferenceLine, Scatter, ScatterChart, XAxis, YAxis,
} from 'recharts';
import { AlertTriangleIcon, XCircleIcon } from 'lucide-react';

import { api, type QcEvidence, type QcResponsePoint } from '@/lib/api';
import { directionOf } from '@/lib/locale';
import { useContent, useLabel, useNumber, useT } from '@/lib/i18n';
import { EmptyState } from '@/components/empty-state';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig,
} from '@/components/ui/chart';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

/**
 * The evidence behind one QC hold (`plan-preproduction` V1).
 *
 * The review's finding was that QC review *"asks for a judgement and supplies no
 * evidence"*: the reviewer sees the flags, a one-line hint and a Release button,
 * and their only route to look closer is a link to the score whose validity is
 * the question. The question actually being asked — is `straight_lining` an
 * invalid protocol or a genuinely consistent responder — *"has an answer in the
 * response data and none at all in the score"*. This draws that answer, at the
 * point the decision is taken.
 *
 * ### Four rules this component is built out of
 *
 * **1. The statistic is the evidence, and the server owns it.** `straight_lining`
 * does not mean "the answers look flat", it means a configured proportion of the
 * scalar answers shared one value over a minimum count. So nothing here
 * re-derives a rule: `dominantValue`, `proportion`, `threshold` and
 * `belowThreshold` all arrive decided, and this file only draws them. A console
 * that compared `responseTimeMs < fastMs` itself would put the rule in two
 * places, and the first time a threshold moved this screen would be arguing a
 * case the server had stopped making.
 *
 * **2. An unanswered item is a GAP, never a zero.** `answered: false` carries no
 * `value`, and both charts are keyed on position rather than on a dense array,
 * so a skipped item leaves a hole in the sequence — which is what it is. Drawing
 * it at zero would invent the strongest possible evidence of speeding.
 *
 * **3. `configResolved: false` is stated, not filled in.** The thresholds come
 * from the config version this result was scored under. When that version is no
 * longer retained, `detectors` is absent and no point carries `belowThreshold` —
 * and the honest render is to say so. Falling back to the module's CURRENT
 * config would print a rule this run was never judged by, beside flags produced
 * by a different one: every number would look plausible, which is worse than a
 * gap because it reads as an answer.
 *
 * **4. Prompts follow `administeredLocale`, not the operator.** This is the
 * take-test harness's rule (CLAUDE.md §28c) and it applies here for the same
 * reason: the reviewer is judging what the CANDIDATE met. An operator reading
 * the console in English, reviewing an Arabic administration, sees the Arabic
 * prompt with their own language beneath it — see *and* know what they are
 * reading. Everything around the prompts is console chrome and stays theirs.
 *
 * ### Colour (dataviz skill, step 2)
 *
 * Two series on each chart, and both are the SAME distinction — "this point is
 * what the detector reacted to" versus "this point is ordinary" — so it is
 * status, not identity. `--chart-1` carries the ordinary mark and the status
 * token carries the finding, which is the one thing a status colour is reserved
 * for. Never colour alone: the strip chart also changes the mark's SHAPE
 * (square, not circle) for the repeated value, both charts carry a legend with
 * text, and every value is in the table below. Both figures are pinned `ltr` by
 * `ChartContainer` (recharts is not direction-aware); their legends sit outside
 * it and read in the page's direction.
 */

/** The finding colour. Reserved for status, and never used as "series 2". */
const FINDING = 'var(--status-warn, var(--destructive))';
const ORDINARY = 'var(--chart-1)';

/* ------------------------------------------------------------------------ */
/*  Small pieces                                                             */
/* ------------------------------------------------------------------------ */

/**
 * A hand-built legend rather than `<ChartLegend>`.
 *
 * Two reasons, and the second is the one that decides it: recharts' own legend
 * lives inside the figure, which `ChartContainer` pins to `ltr` — so in Arabic
 * the swatch and its label would read left-to-right inside an otherwise
 * right-to-left card. And the latency chart colours per-point rather than
 * per-series, so a series legend would describe two series that do not exist.
 */
function ChartKey({ entries }: { entries: Array<{ colour: string; label: string; square?: boolean }> }) {
  return (
    <ul className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {entries.map((entry) => (
        <li key={entry.label} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={entry.square ? 'size-2.5' : 'size-2.5 rounded-full'}
            style={{ background: entry.colour }}
          />
          {entry.label}
        </li>
      ))}
    </ul>
  );
}

/** One detector's verdict: what it measured, and whether it fired. */
function Detector({ title, fired, children }: {
  title: string; fired: boolean; children: ReactNode;
}) {
  const t = useT();
  return (
    <div className="rounded-md border p-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium">{title}</h4>
        {/* Never colour alone — the badge carries the word. */}
        <Badge variant={fired ? 'destructive' : 'secondary'}>
          {t(fired ? 'qcEvidence.fired' : 'qcEvidence.didNotFire')}
        </Badge>
      </div>
      <div className="text-muted-foreground space-y-1 text-xs">{children}</div>
    </div>
  );
}

/**
 * The answer as recorded — a number, an option, or an ordering of options.
 *
 * `answered: false` is the first branch and produces a WORD, not a dash and not
 * a zero: on a 0–100 slider "answered 0" and "did not answer" are different
 * facts, and the contract keeps `value` absent precisely so a console cannot
 * merge them.
 */
function Answer({ point, locale }: { point: QcResponsePoint; locale: string }) {
  const t = useT();
  const number = useNumber();
  const administered = useContent(locale);

  if (!point.answered || point.value === undefined) {
    return <span className="text-muted-foreground italic">{t('qcEvidence.unanswered')}</span>;
  }

  const labelOf = (optionId: string) => {
    const option = (point.options ?? []).find((o) => o.optionId === optionId);
    return option ? administered(option.label) : optionId;
  };

  if (typeof point.value === 'number') {
    return (
      <span className="tabular-nums">
        {number(point.value)}
        {point.scaleMin !== undefined && point.scaleMax !== undefined && (
          <span className="text-muted-foreground ms-1">
            {t('itemAnalysis.scale', {
              min: number(point.scaleMin),
              max: number(point.scaleMax),
            })}
          </span>
        )}
      </span>
    );
  }

  if (Array.isArray(point.value)) {
    // A ranking: the ORDER is the answer, so it is rendered as one.
    return <span dir={directionOf(locale)}>{point.value.map(labelOf).join(' → ')}</span>;
  }

  return <span dir={directionOf(locale)}>{labelOf(point.value)}</span>;
}

/* ------------------------------------------------------------------------ */
/*  The panel                                                                */
/* ------------------------------------------------------------------------ */

export function QcEvidencePanel({ resultId }: { resultId: string }) {
  const t = useT();

  const evidence = useQuery({
    queryKey: ['qc-evidence', resultId],
    queryFn: () => api.qcEvidence(resultId),
  });

  if (evidence.isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-live="polite">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  if (evidence.error || !evidence.data) {
    return (
      <EmptyState
        icon={XCircleIcon}
        title={t('qcEvidence.loadFailed')}
        description={
          evidence.error instanceof Error ? evidence.error.message : t('table.errorDescription')
        }
      />
    );
  }

  return <Evidence evidence={evidence.data} />;
}

function Evidence({ evidence }: { evidence: QcEvidence }) {
  const t = useT();
  const label = useLabel();
  const number = useNumber();
  const operatorContent = useContent();
  /** Rule 4: the prompts are the candidate's language, not the reader's. */
  const administered = useContent(evidence.administeredLocale);
  const administeredDir = directionOf(evidence.administeredLocale);

  const { pattern, detectors } = evidence;
  const straight = detectors?.straightLining;
  const speeding = detectors?.speeding;

  /* --------------------------------------------------------- the strip chart */

  const scalarAnswered = pattern.filter(
    (p) => p.scalar && p.answered && typeof p.value === 'number',
  );
  /**
   * Whether every plotted item shares one response scale.
   *
   * If they do not, the heights are not comparable — a 5 on a 1–5 likert and a 5
   * on a 0–100 slider are not the same mark. The chart still draws the raw
   * values, because that is what the detector compared, and the caption says
   * plainly that the heights cannot be read against each other. Silently
   * rescaling them would be inventing a transform the platform never applied.
   */
  const scaleKeys = new Set(scalarAnswered.map((p) => `${p.scaleMin}:${p.scaleMax}`));
  const first = scalarAnswered[0];
  const oneScale =
    scaleKeys.size <= 1 && first?.scaleMin !== undefined && first?.scaleMax !== undefined;

  const dominant = straight?.dominantValue;
  const isRepeated = (p: QcResponsePoint) => dominant !== undefined && p.value === dominant;

  const stripPoint = (p: QcResponsePoint) => ({ position: p.position, value: p.value as number });
  const ordinaryPoints = scalarAnswered.filter((p) => !isRepeated(p)).map(stripPoint);
  const repeatedPoints = scalarAnswered.filter(isRepeated).map(stripPoint);

  const stripConfig = {
    value: { label: t('qcEvidence.pattern.axisValue'), color: ORDINARY },
  } satisfies ChartConfig;

  /* ------------------------------------------------------- the latency chart */

  /**
   * Every position, so an untimed item is a HOLE rather than a shifted bar.
   *
   * `belowThreshold` is read, never computed: it is absent for an untimed item
   * and absent for every item when the pinned config is gone, and in both cases
   * the honest render is the ordinary colour with no claim attached.
   */
  const latency = pattern.map((p) => ({
    position: p.position,
    normal: p.responseTimeMs !== undefined && !p.belowThreshold ? p.responseTimeMs : undefined,
    below: p.belowThreshold ? p.responseTimeMs : undefined,
  }));
  const anyTimed = pattern.some((p) => p.responseTimeMs !== undefined);

  const latencyConfig = {
    normal: { label: t('qcEvidence.latency.normal'), color: ORDINARY },
    below: { label: t('qcEvidence.latency.below'), color: FINDING },
  } satisfies ChartConfig;

  /* ------------------------------------------------------------- the flags */

  const stored = [...evidence.qcFlags].sort();
  const recomputed = detectors ? [...detectors.flags].sort() : null;
  /**
   * A DISAGREEMENT IS A FINDING, not a render bug.
   *
   * Both sets come from the same function over the same stored responses under
   * the same pinned config, so they should be identical. If they are not, the
   * responses or the retained config changed after scoring — which is a fact
   * about the platform, and the reviewer needs it before they decide anything
   * about the candidate. Publishing both is what makes it visible; quietly
   * showing one would resolve it in favour of whichever we happened to pick.
   */
  const disagree = recomputed !== null && stored.join('|') !== recomputed.join('|');

  const flagList = (flags: string[]) =>
    flags.length === 0
      ? <span className="text-muted-foreground text-xs">{t('qcEvidence.flags.none')}</span>
      : flags.map((flag) => (
          <Badge key={flag} variant="destructive">{label('qcFlag', flag)}</Badge>
        ));

  return (
    <div className="space-y-5">
      {/* ------------------------------------------------------------ facts */}
      <div className="text-muted-foreground space-y-1 text-xs">
        {/* An opaque reference, and the only thing about a person this platform
            holds. `dir="ltr"` because it is an identifier, not prose. */}
        <p>
          <bdi className="font-mono">{evidence.candidateRef}</bdi>
          {' · '}
          {t('qcEvidence.answeredOf', {
            answered: number(evidence.answeredCount),
            items: number(evidence.itemCount),
          })}
        </p>
        <p>
          {t('qcEvidence.scoredUnder', {
            version: evidence.configVersion,
            instrument: evidence.instrumentVersion,
          })}
        </p>
        <p>
          {t('qcEvidence.administeredIn', {
            language: label('languageName', evidence.administeredLocale),
          })}
        </p>
      </div>

      {/* ------------------------------------------------------------ flags */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">{t('qcEvidence.flags.stored')}</span>
          {flagList(stored)}
        </div>
        {recomputed && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">{t('qcEvidence.flags.recomputed')}</span>
            {flagList(recomputed)}
          </div>
        )}
      </div>

      {disagree && (
        <Alert variant="destructive">
          <AlertTriangleIcon />
          <AlertTitle>{t('qcEvidence.flags.disagree.title')}</AlertTitle>
          <AlertDescription>{t('qcEvidence.flags.disagree.body')}</AlertDescription>
        </Alert>
      )}

      {/* --------------------------------------------------------- verdicts */}
      {!evidence.configResolved ? (
        /**
         * Rule 3. NOT a blank panel and NOT a default: the sentence names the
         * version and says what is missing, because "no thresholds" and "the
         * screen failed to load them" must not look the same.
         */
        <Alert>
          <AlertTriangleIcon />
          <AlertTitle>{t('qcEvidence.configGone.title')}</AlertTitle>
          <AlertDescription>
            {t('qcEvidence.configGone.body', { version: evidence.configVersion })}
          </AlertDescription>
        </Alert>
      ) : straight && speeding ? (
        <section aria-labelledby="qc-verdicts" className="space-y-2">
          <h3 id="qc-verdicts" className="text-sm font-medium">{t('qcEvidence.verdict.title')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Detector title={t('qcEvidence.straightLining.title')} fired={straight.flagged}>
              {straight.proportion === undefined || straight.dominantValue === undefined ? (
                <p>{t('qcEvidence.straightLining.nothingAnswered')}</p>
              ) : (
                <p className="text-foreground">
                  {t('qcEvidence.straightLining.stat', {
                    proportion: number(straight.proportion),
                    n: number(straight.n),
                    value: number(straight.dominantValue),
                    threshold: number(straight.threshold),
                  })}
                </p>
              )}
              {straight.n < straight.minimumN && (
                <p>
                  {t('qcEvidence.straightLining.belowMinimum', {
                    n: number(straight.n),
                    minimum: number(straight.minimumN),
                  })}
                </p>
              )}
              <p>{t('qcEvidence.straightLining.denominator')}</p>
            </Detector>

            <Detector title={t('qcEvidence.speeding.title')} fired={speeding.flagged}>
              {speeding.medianMs === undefined ? (
                <p>{t('qcEvidence.speeding.untimed')}</p>
              ) : (
                <>
                  <p className="text-foreground">
                    {t('qcEvidence.speeding.stat', {
                      median: t('qcEvidence.ms', { ms: number(speeding.medianMs) }),
                      n: number(speeding.n),
                      threshold: t('qcEvidence.ms', { ms: number(speeding.thresholdMs) }),
                    })}
                  </p>
                  {/* Descriptive only, and it says so — presenting this as the
                      trigger would invent a second rule. */}
                  <p>
                    {t('qcEvidence.speeding.below', {
                      count: number(speeding.belowThreshold),
                      n: number(speeding.n),
                    })}
                  </p>
                </>
              )}
            </Detector>
          </div>
        </section>
      ) : null}

      {/* ----------------------------------------------------- strip chart */}
      <section aria-labelledby="qc-pattern" className="space-y-2">
        <h3 id="qc-pattern" className="text-sm font-medium">{t('qcEvidence.pattern.title')}</h3>
        <p className="text-muted-foreground text-xs">{t('qcEvidence.pattern.hint')}</p>

        {scalarAnswered.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('qcEvidence.pattern.noScalar')}</p>
        ) : (
          <>
            <ChartContainer config={stripConfig} className="max-h-56 w-full">
              <ScatterChart margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
                <CartesianGrid stroke="var(--border)" />
                <XAxis
                  type="number"
                  dataKey="position"
                  allowDecimals={false}
                  domain={[0, Math.max(0, evidence.itemCount - 1)]}
                  tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                  name={t('qcEvidence.pattern.axisPosition')}
                />
                <YAxis
                  type="number"
                  dataKey="value"
                  width={36}
                  domain={oneScale ? [first!.scaleMin!, first!.scaleMax!] : ['auto', 'auto']}
                  tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                  name={t('qcEvidence.pattern.axisValue')}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                {/* ≥8px marks with a 2px surface ring, so overlapping points at
                    the same value stay countable. */}
                <Scatter
                  data={ordinaryPoints}
                  dataKey="value"
                  fill={ORDINARY}
                  stroke="var(--card)"
                  strokeWidth={2}
                />
                {repeatedPoints.length > 0 && (
                  <Scatter
                    data={repeatedPoints}
                    dataKey="value"
                    fill={FINDING}
                    stroke="var(--card)"
                    strokeWidth={2}
                    // SHAPE as well as colour: identity is never colour alone.
                    shape="square"
                  />
                )}
              </ScatterChart>
            </ChartContainer>

            {/* The second entry appears only when there is genuinely a second
                series to describe — keyed on the marked POINTS, not merely on
                the detector having named a dominant value, so the legend can
                never promise a mark the chart did not draw. */}
            <ChartKey
              entries={[
                { colour: ORDINARY, label: t('qcEvidence.pattern.answer') },
                ...(repeatedPoints.length > 0 && dominant !== undefined
                  ? [{
                      colour: FINDING,
                      square: true,
                      label: t('qcEvidence.pattern.repeated', { value: number(dominant) }),
                    }]
                  : []),
              ]}
            />

            {!oneScale && (
              <p className="text-muted-foreground text-xs">{t('qcEvidence.pattern.mixedScales')}</p>
            )}
          </>
        )}
      </section>

      {/* --------------------------------------------------- latency chart */}
      <section aria-labelledby="qc-latency" className="space-y-2">
        <h3 id="qc-latency" className="text-sm font-medium">{t('qcEvidence.latency.title')}</h3>
        <p className="text-muted-foreground text-xs">{t('qcEvidence.latency.hint')}</p>

        {!anyTimed ? (
          <p className="text-muted-foreground text-sm">{t('qcEvidence.latency.none')}</p>
        ) : (
          <>
            <ChartContainer config={latencyConfig} className="max-h-56 w-full">
              <BarChart data={latency} margin={{ left: 8, right: 16, top: 8 }}>
                <CartesianGrid vertical={false} stroke="var(--border)" />
                <XAxis
                  dataKey="position"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                />
                <YAxis
                  width={48}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                {/* One stack, and every point fills exactly one of the two —
                    so the bar's height is the response time and its colour is
                    the server's already-made comparison. */}
                <Bar dataKey="normal" stackId="ms" fill="var(--color-normal)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="below" stackId="ms" fill="var(--color-below)" radius={[4, 4, 0, 0]} />
                {speeding && (
                  // Dashed BECAUSE it is a threshold rather than a gridline —
                  // the grid above it is solid and recessive for the same reason.
                  <ReferenceLine
                    y={speeding.thresholdMs}
                    stroke={FINDING}
                    strokeDasharray="4 4"
                    strokeWidth={2}
                  />
                )}
              </BarChart>
            </ChartContainer>

            <ChartKey
              entries={[
                { colour: ORDINARY, label: t('qcEvidence.latency.normal') },
                { colour: FINDING, label: t('qcEvidence.latency.below') },
              ]}
            />

            {!evidence.configResolved && (
              <p className="text-muted-foreground text-xs">{t('qcEvidence.latency.noThreshold')}</p>
            )}
            {speeding && (
              <p className="text-muted-foreground text-xs">
                {t('qcEvidence.latency.threshold')}
                {': '}
                {t('qcEvidence.ms', { ms: number(speeding.thresholdMs) })}
              </p>
            )}
          </>
        )}
      </section>

      {/* --------------------------------------------------- the whole run */}
      <section aria-labelledby="qc-items" className="space-y-2">
        <h3 id="qc-items" className="text-sm font-medium">{t('qcEvidence.table.title')}</h3>

        {pattern.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('qcEvidence.empty')}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">{t('qcEvidence.column.position')}</TableHead>
                  <TableHead>{t('qcEvidence.column.item')}</TableHead>
                  <TableHead>{t('qcEvidence.column.answer')}</TableHead>
                  <TableHead className="w-28">{t('qcEvidence.column.time')}</TableHead>
                  <TableHead className="w-40">{t('qcEvidence.column.key')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pattern.map((point) => {
                  const gloss = operatorContent(point.prompt);
                  const asAdministered = administered(point.prompt);
                  return (
                    <TableRow key={`${point.position}:${point.itemId}`}>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {number(point.position + 1)}
                      </TableCell>
                      <TableCell>
                        {/* The administration first — this is what the candidate
                            read. The operator's language beneath it, and only
                            when it differs, so an English console reviewing an
                            English run does not print everything twice. */}
                        <p className="text-sm" dir={administeredDir}>{asAdministered}</p>
                        {gloss !== asAdministered && (
                          <p
                            className="text-muted-foreground text-xs"
                            dir={administeredDir === 'rtl' ? 'ltr' : 'rtl'}
                          >
                            {gloss}
                          </p>
                        )}
                        <p className="text-muted-foreground text-xs">
                          {label('itemType', point.itemType)}
                          {point.scalar && ` · ${t('qcEvidence.scalar')}`}
                        </p>
                      </TableCell>
                      <TableCell className="text-sm">
                        <Answer point={point} locale={evidence.administeredLocale} />
                      </TableCell>
                      <TableCell className="text-xs tabular-nums">
                        {point.responseTimeMs === undefined
                          ? <span className="text-muted-foreground">—</span>
                          : (
                            <span className={point.belowThreshold ? 'font-medium' : undefined}>
                              {t('qcEvidence.ms', { ms: number(point.responseTimeMs) })}
                              {point.belowThreshold && (
                                <span className="block">{t('qcEvidence.latency.below')}</span>
                              )}
                            </span>
                          )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {point.dimension ? (
                          <span>
                            {point.dimension}
                            {point.reverseKeyed && (
                              <span className="text-muted-foreground block">
                                {t('qcEvidence.reverseKeyed')}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <p className="text-muted-foreground text-xs">{t('qcEvidence.keyNote')}</p>
      </section>
    </div>
  );
}
